import { Box, Button, Card, CardContent, Typography, Link } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { Svgs } from '../../assets/svg';
import { Column, Row, Searchbox, Spacer } from '../../components';
import { MutatorState } from '@aesop-fables/scrinium';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { API_URL } from '../../api/apis';
import { OrganizationMetricsData } from '../../api/apis/OrgPricebookLicenseMetricsApi';
import { useLoading } from '../../hooks/useLoading';
import { OrganizationData } from '../../models/OrganizationData';
import { OrgStatus } from '../multitier/AdminDashboard';

export interface OrgListViewProps {
  orgList: OrganizationData[];
  allMetrics: OrganizationMetricsData[];
  select: MutatorState<number>;
  setOrgStatus: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
}

export const OrgListView: React.FC<OrgListViewProps> = ({
  orgList,
  allMetrics,
  select,
  setOrgStatus,
}) => {
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const [searchWord, setSearchWord] = useState('');

  const numCompleteOrgs = allMetrics.length ?? 0;
  const enoughOrgs = numCompleteOrgs >= 10;

  const filteredOrgDataForTable = useMemo(() => {
    if (!searchWord) return allMetrics;
    const lowerQuery = searchWord.toLowerCase();
    return allMetrics.filter(item =>
      Object.values(item).some(val => val && val.toString().toLowerCase().includes(lowerQuery)),
    );
  }, [searchWord, allMetrics]);

  const selectOrganization = async (orgId: number) => {
    setOrgStatus('complete');
    setLoading(true);
    await select.action(orgId);
  };

  const onView = async (orgId: number | undefined) => {
    if (orgId === undefined) {
      setToast(new Toast({ message: 'No organization id found', severity: 'error', open: true }));
      return;
    }
    setLoading(true);
    selectOrganization(orgId);
  };

  return (
    <>
      {enoughOrgs && (
        <>
          <Spacer height='xs' />
          <Card variant='ghost' color='primary'>
            <CardContent className='searchbox'>
              <Column>
                <Searchbox
                  callback={value => setSearchWord(value)}
                  label='Search'
                  placeholder='Search by organization name'
                />
              </Column>
            </CardContent>
          </Card>
        </>
      )}

      <Spacer height='xs' />
      <Card variant='ghost' color='primary'>
        <CardContent
          className='pad25'
          sx={{ textAlign: filteredOrgDataForTable?.length > 0 ? undefined : 'center' }}>
          {numCompleteOrgs === 0 ? (
            <Typography
              variant='p20'
              color='secondary.main'
              textAlign='center'
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              You haven't created any organizations yet.
            </Typography>
          ) : filteredOrgDataForTable?.length > 0 ? (
            filteredOrgDataForTable?.map((org, i) => {
              const orgData = orgList.filter(x => x.id === org.organizationId).length > 0;
              return (
                <Card
                  variant='ghost'
                  color='secondary'
                  key={org.organizationId}
                  sx={{ marginTop: i === 0 ? '0px' : '10px' }}>
                  <CardContent
                    sx={{ padding: '13px 20px !important', containerType: 'inline-size' }}>
                    <Row style={{ justifyContent: 'space-between' }}>
                      <Row
                        style={{
                          alignItems: 'center',
                        }}>
                        <Svgs.IconOrgPaysLarge />
                      </Row>
                      <Row
                        style={{
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flex: '2 0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        <Spacer width='xxs' />
                        <Link
                          className='darkenOnHover'
                          noWrap={true}
                          variant='p18SemiBold'
                          color='secondary.main'
                          onClick={() => {
                            onView(org.organizationId);
                          }}>
                          {org.organizationName}
                        </Link>
                        {orgData && orgList.filter(x => x.id === org.organizationId)[0].logoUri && (
                          <Box
                            sx={{
                              'display': 'none',
                              '@container (min-width: 530px)': { display: 'contents' },
                            }}>
                            <Box sx={{ flex: 2 }} />
                            <Spacer width='xs' />
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                              }}>
                              <Box
                                component='img'
                                src={`${API_URL}organization/logo/${org.organizationId}`}
                                alt={`${org.organizationName}'s logo`}
                                sx={{
                                  height: '35px',
                                  maxWidth: '100%',
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }} />
                          </Box>
                        )}
                      </Row>

                      <Box
                        sx={{
                          width: { xs: '15px', lg: '20px', xl: '50px' },
                        }}
                      />
                      <Row style={{ alignItems: 'center' }}>
                        <Column>
                          <Typography variant='p12SemiBold' color='secondary.main'>
                            Active agreements
                          </Typography>
                          <Row
                            style={{
                              alignItems: 'center',
                            }}>
                            <Svgs.IconLicenseDefault />
                            <Spacer width='xxs' />
                            <Typography variant='p20SemiBold' color='secondary.main'>
                              {org.totalLicenseAgreements}
                            </Typography>
                          </Row>
                        </Column>
                        <Box sx={{ width: { xs: '15px', lg: '40px' } }} />
                        <Column>
                          <Typography variant='p12SemiBold' color='secondary.main'>
                            Active users
                          </Typography>
                          <Row
                            style={{
                              alignItems: 'center',
                            }}>
                            <Svgs.IconMembersDefault />
                            <Spacer width='xxs' />
                            <Typography variant='p20SemiBold' color='secondary.main'>
                              {org.activeUsers}
                            </Typography>
                          </Row>
                        </Column>

                        <Box
                          sx={{
                            width: { xs: '15px', md: '20px', lg: '40px' },
                          }}
                        />
                        <Button
                          onClick={() => onView(org.organizationId)}
                          className='cardAction'
                          color='secondary'>
                          View
                        </Button>
                      </Row>
                    </Row>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Row style={{ justifyContent: 'center', padding: '5.5px 0' }}>
              <Typography variant='p18SemiBold' color='secondary.main'>
                No results found
              </Typography>
            </Row>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default OrgListView;
